<script setup>
import { debounce } from '@tools/debounce';
import Flickity from 'flickity';
import { ref, onMounted, nextTick } from 'vue';

const props = defineProps({
  height: {
    type: Number,
    default: 230,
  },
  options: {
    type: Object,
    default: () => ({
      cellAlign: 'right',
      wrapAround: true,
      watchCSS: true,
      lazyLoad: 1,
    }),
  },
  resizeBreakpoints: {
    type: Array,
    default: undefined,
  },
});

const carousel = ref(null);
const rendered = ref(true);
const flickity = ref(null);

const initFlickity = async () => {
  if (flickity.value) {
    // while destroying old flickity instance, we have to force rerender step by step to avoid conflicts between vue component and flickity
    rendered.value = false;
    flickity.value.destroy();
    await nextTick();
    rendered.value = true;
    await nextTick();
  }

  if (!carousel.value) {
    return;
  }

  flickity.value = new Flickity(carousel.value, props.options);

  const flickityViewport = carousel.value.querySelector('.flickity-viewport');
  if (!flickityViewport) {
    return;
  }

  // Set the default height even if there's no content (e.g. draft in my offers)
  flickityViewport.style.height = `${props.height}px`;
  flickityViewport.style.width = '100%';

  const flickitySlider = carousel.value.querySelector('.flickity-slider');

  flickitySlider.style.transition = 'transform 0.3s ease';

  const next = carousel.value.querySelector('.flickity-prev-next-button.next');
  const previous = carousel.value.querySelector('.flickity-prev-next-button.previous');

  // When an image is loaded via lazy-load the height is reset, we don't want that.
  flickity.value.on('lazyLoad', () => {
    flickityViewport.style.height = `${props.height}px`;
  });

  const handleButtonClick = (event) => {
    flickity.value.reposition();
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const handleButtonDblClick = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  if (next) {
    next.addEventListener('click', handleButtonClick);
    next.addEventListener('dblclick', handleButtonDblClick);
  }

  if (previous) {
    previous.addEventListener('click', handleButtonClick);
    previous.addEventListener('dblclick', handleButtonDblClick);
  }
};

const handleResize = async () => {
  if (props.resizeBreakpoints && !props.resizeBreakpoints.includes(window.innerWidth)) {
    return;
  }

  await initFlickity();
};

onMounted(() => {
  initFlickity();
  window.addEventListener('resize', debounce(handleResize, 150));
});
</script>

<template>
  <div
    v-if="rendered"
    ref="carousel"
    class="cw-card__image carousel carousel--inner"
    @dblclick.stop
  >
    <slot />
  </div>
</template>
